import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState({ status: "checking" });
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState({ status: "error" }); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState({ status: "paid", plan: data.plan, kind: data.kind, credits: data.credits });
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ status: "failed" }); return; }
        if (polls.current >= MAX_POLLS) { setState({ status: "pending" }); return; }
        polls.current += 1;
        timer = setTimeout(poll, 2000);
      } catch {
        setState({ status: "error" });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refreshUser]);

  return (
    <div className="payment-result" data-testid="payment-success-page">
      {state.status === "checking" && (
        <div className="pay-card"><Loader2 className="spin" size={46} /><h1>Confirming your payment…</h1><p>Hang tight while we activate your plan.</p></div>
      )}
      {state.status === "paid" && (
        <div className="pay-card" data-testid="payment-success-confirmed">
          <span className="pay-ic ok"><CheckCircle2 size={40} /></span>
          {state.kind === "credits" ? <>
            <h1>{Number(state.credits).toLocaleString()} credits added</h1>
            <p>Your Studio balance has been topped up.</p>
            <button className="primary-btn" onClick={() => nav("/studio/credits")} data-testid="payment-go-credits">View credits</button>
          </> : <>
            <h1>You're on {state.plan}!</h1>
            <p>Your plan is active, including your monthly Studio credits.</p>
            <button className="primary-btn" onClick={() => nav("/dashboard")} data-testid="payment-go-dashboard">Go to dashboard</button>
          </>}
        </div>
      )}
      {state.status === "pending" && (
        <div className="pay-card"><span className="pay-ic warn"><Loader2 size={40} /></span><h1>Payment is processing</h1><p>This can take a moment. Your plan will update automatically once it clears.</p><button className="secondary-btn" onClick={() => nav("/plans")}>Back to plans</button></div>
      )}
      {(state.status === "failed" || state.status === "error") && (
        <div className="pay-card" data-testid="payment-success-error">
          <span className="pay-ic err"><XCircle size={40} /></span>
          <h1>We couldn't confirm the payment</h1>
          <p>No plan change was made. You can try again anytime.</p>
          <Link to="/plans" className="primary-btn" data-testid="payment-retry">Back to plans</Link>
        </div>
      )}
    </div>
  );
}
